/**
 * Packaging Material Emission Factor edit view — story 36911.
 * Structural base: packaging-pcf/processing-activities/process. Field-level reference:
 * modules/emission-factors/emission.
 */

import { FC, useCallback, useEffect, useMemo, useState, EffectCallback } from 'react'
import get from 'lodash/get'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Breadcrumbs, Button, LottieLoader, Notification } from '@mondra/ui-components'
import { useFormik } from 'formik'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'
import { fetchMaterialEmissionFactor, getConfiguration } from './materialEmissionFactorActions'
import { showCorrelationIdError } from '../../../../redux/correlationIdSlice'
import {
  handlePublishStatus,
  setDefaultMaterialEmissionFactor,
} from '../../../../redux/materialEmissionFactorSlice'
import {
  createMaterialEmissionFactor,
  updateMaterialEmissionFactor,
  publishMaterialEmissionFactor,
  unPublishMaterialEmissionFactor,
} from './api'
import {
  formSchema,
  hasUnsavedChanges,
  TAB_ERRORS,
  TAB_LABELS,
  PRODUCT_TYPE_PACKAGING_MATERIAL,
  TabsEnum,
  toNumberOrNull,
} from './utils'
import { usePrompt } from '../../../../hooks/usePrompt'
import { transformFormValues } from '../../../../shared/formFunctions'
import { ContentTabs } from '../../../../components/ContentTabs'
import { RecordHeader } from '../../../../components/RecordHeader'
import DataQualitySection, {
  PACKAGING_REFERENCE_TECHNOLOGY_PROXY_CONTRIBUTION_DESCRIPTION,
  PACKAGING_REFERENCE_TECHNOLOGY_TYPE_DESCRIPTION,
} from '../../../../components/ProcessingActivitySections/DataQualitySection'
import ActivityRightPanel from '../../../../components/ActivityRightPanel'
import UnsavedChangesWarning from '../../../../components/UnsavedChangesWarning'
import { ActivityTypes, ROUTES } from '../../../../const'
import { MaterialInfo } from './components/MaterialInfo'
import { DataOrigin } from './components/DataOrigin'
import { Emissions } from './components/Emissions'
import { Impacts } from './components/Impacts'
import './index.scss'

// A publish rejection carries FluentValidation errors, not a ProblemDetails title: reading .title
// alone shows an empty notification for exactly the failures worth reading.
const toServerErrorMessage = (error: any): string => {
  const data = error.response?.data

  if (typeof data === 'string') {
    return data
  }
  if (Array.isArray(data)) {
    return data
      .map((item: any) => item.errorMessage ?? item.ErrorMessage)
      .filter(Boolean)
      .join('\n')
  }
  if (data?.errors && typeof data.errors !== 'string') {
    return Object.values(data.errors).flat().join('\n')
  }
  return data?.title ?? data?.errors ?? error.message ?? 'Something went wrong.'
}

const TAB_ORDER = [
  TabsEnum.MATERIAL,
  TabsEnum.DATA_ORIGIN,
  TabsEnum.EMISSIONS,
  TabsEnum.IMPACTS,
  TabsEnum.DATA_QUALITY,
]

const MaterialEmissionFactor: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    loading,
    materialEmissionFactor: factor,
    configuration,
  } = useAppSelector(state => state.materialEmissionFactorSlice)
  const [showNotification, setShowNotification] = useState<boolean>(false)
  const [showError, setShowError] = useState<string>('')
  const [errors, setErrors] = useState<[any, string[]][]>()
  const [showPublished, setShowPublished] = useState<boolean>(false)
  const [selectedTab, setSelectedTab] = useState<number>(0)
  const factorId = searchParams.get('itemId') || ''

  useEffect((): ReturnType<EffectCallback> => {
    if (factorId === '') {
      navigate('/not-found')
      return
    }
    dispatch(getConfiguration())
    if (factorId !== 'new') {
      dispatch(
        fetchMaterialEmissionFactor(factorId, () => navigate(ROUTES.materialEmissionFactors))
      )
    }
    return () => {
      dispatch(setDefaultMaterialEmissionFactor())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formik = useFormik({
    initialValues: { ...factor },
    validationSchema: formSchema,
    onSubmit: async (values, { setSubmitting }) => {
      // Nothing to publish until the record exists.
      if (factorId === 'new') {
        setSubmitting(false)
        return
      }

      const isPublishing = factor.state === 0

      try {
        if (isPublishing) {
          await publishMaterialEmissionFactor(factorId)
          dispatch(handlePublishStatus(1))
        } else {
          await unPublishMaterialEmissionFactor(factorId)
          dispatch(handlePublishStatus(0))
        }
        setShowPublished(true)
        setTimeout(() => setShowPublished(false), 4000)
      } catch (error: any) {
        setShowError(toServerErrorMessage(error))
      } finally {
        // Formik leaves isSubmitting true for a handler that does not settle it, which would
        // disable Save for the rest of the page's life.
        setSubmitting(false)
      }
    },
  })

  // Category is form state, not a wire field, so a saved record opens with it empty. Deriving it
  // from the material leaf is what gives the Material name select something to filter by - without
  // it, editing a saved record shows an empty Category and an unfiltered Material name.
  useEffect(() => {
    const categoryId =
      configuration.packagingMaterialsTaxonomies.functionalUnits.find(
        fu => fu.id === factor.productId
      )?.categoryId ?? null

    formik.setValues({ ...factor, packagingMaterialCategoryId: categoryId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factor, configuration.packagingMaterialsTaxonomies.functionalUnits])

  const getTabErrors = useCallback(
    (tab: TabsEnum) =>
      TAB_ERRORS[tab].some(value => get(formik.touched, value) && !!get(formik.errors, value)),
    [formik.touched, formik.errors]
  )

  const tabs = TAB_ORDER.map(tab => ({
    label: TAB_LABELS[tab],
    hasErrors: getTabErrors(tab),
  }))

  // Changing Category invalidates the selected Material name: the material list is filtered by
  // category, and a pair that disagrees is rejected by the BE's packaging rules.
  const handleCategoryChange = useCallback(
    (e: React.ChangeEvent<Element>) => {
      formik.setValues({
        ...formik.values,
        packagingMaterialCategoryId: toNumberOrNull((e.target as HTMLSelectElement).value),
        productId: null,
      })
    },
    [formik]
  )

  const handleDataQualityFieldChange = useCallback(e => formik.handleChange(e), [formik])

  const handleCancel = useCallback(() => navigate(ROUTES.materialEmissionFactors), [navigate])

  const saveDraft = useCallback(
    async e => {
      e.preventDefault()
      if (formik.isSubmitting) {
        return
      }
      formik.setSubmitting(true)
      setErrors(undefined)
      setShowError('')

      const { values } = formik
      // Every numeric field arrives from an Input or Select as a string, and unset selects as -1.
      // The wire contract is numbers and nulls, so coerce the whole object first - exactly what the
      // food emission factor form does - then re-apply the fields with their own shape below.
      const payload = {
        ...transformFormValues(values),
        // Fixed, not user-editable: the form renders it disabled, so it is set here rather than
        // read back off the values, which keeps a stale or absent form value out of the payload.
        productType: PRODUCT_TYPE_PACKAGING_MATERIAL,
        productId: toNumberOrNull(values.productId),
        functionalUnit: toNumberOrNull(values.functionalUnit),
        countryId: toNumberOrNull(values.countryId),
        regionIdentifierId: toNumberOrNull(values.regionIdentifierId),
        dataOrigin: toNumberOrNull(values.dataOrigin),
        secondaryDataReferenceSourceType: toNumberOrNull(values.secondaryDataReferenceSourceType),
        referenceSourcingCountry: toNumberOrNull(values.referenceSourcingCountry),
        referenceRegionIdentifier: toNumberOrNull(values.referenceRegionIdentifier),
        referencesSamplingYear: toNumberOrNull(values.referencesSamplingYear),
        referenceDataPublicationYear: toNumberOrNull(values.referenceDataPublicationYear),
        // The Virgin/Recycled select carries 0/1, or -1 while unset; the wire contract is a
        // nullable boolean, and an unset value must go over as null rather than as "recycled".
        isRecycled:
          toNumberOrNull(values.isRecycled) === null ? null : Boolean(Number(values.isRecycled)),
        includesProcessingImpacts: Boolean(values.includesProcessingImpacts),
      }

      // Generated by the platform on save, never sent back.
      delete (payload as any).code
      // Form state only: the material leaf carries the category, so it is never sent.
      delete (payload as any).packagingMaterialCategoryId

      try {
        if (factorId === 'new') {
          await createMaterialEmissionFactor(payload)
          navigate(ROUTES.materialEmissionFactors)
        } else {
          await updateMaterialEmissionFactor(factorId, payload as any)
          dispatch(
            fetchMaterialEmissionFactor(factorId, () => navigate(ROUTES.materialEmissionFactors))
          )
          setShowNotification(true)
          setTimeout(() => setShowNotification(false), 5000)
        }
      } catch (error: any) {
        if (error.showCorrelationId) {
          dispatch(showCorrelationIdError(error.CorrelationId))
        } else {
          const data = error.response?.data
          setShowError(
            typeof data === 'string'
              ? data
              : typeof data?.errors === 'string'
              ? data.errors
              : Object.values(data?.errors ?? {})
                  .toString()
                  .replace(/!,/g, '!\n')
          )
          setTimeout(() => setShowError(''), 5000)
          if (data?.errors && typeof data.errors !== 'string') {
            setErrors(Object.entries(data.errors) as [any, string[]][])
          }
        }
      } finally {
        formik.setSubmitting(false)
      }
    },
    [formik, factorId, dispatch, navigate]
  )

  const isDirty = useMemo(() => hasUnsavedChanges(factor, formik.values), [factor, formik.values])

  usePrompt(
    'There are unsaved changes, do you want to leave?',
    factorId !== 'new' ? isDirty : false
  )

  if (loading === true && factorId !== 'new') {
    return (
      <div className="overlay">
        <LottieLoader
          lottieType="butterflyLottie"
          style={{ height: '10.25rem', width: '10.25rem' }}
        />
      </div>
    )
  }

  return (
    <>
      <div className="piece content">
        {showError !== '' && (
          <div className={'piece-notification z-dialog'}>
            <Notification type="error" label="Oops:" description={showError} />
          </div>
        )}
        {showPublished && (
          <div className={'piece-notification z-dialog'}>
            <Notification
              type="success"
              label="Success:"
              description="Status was updated!"
              isToast={false}
            />
          </div>
        )}
        {showNotification && (
          <div className={'piece-notification z-dialog'}>
            <Notification
              type="success"
              label="Success:"
              description="Item was updated!"
              isToast={false}
            />
          </div>
        )}
        <div className="mb-3 mt-3">
          <Breadcrumbs
            routes={[
              {
                path: ROUTES.materialEmissionFactors,
                title: 'Material Emission Factors Table',
              },
              {
                path: `${ROUTES.materialEmissionFactorsItem}?itemId=${factorId}`,
                title: 'Material Emission Factors Item',
              },
            ]}
          />
        </div>
        {factorId !== 'new' && (
          <RecordHeader
            label={factor.code}
            isPublished={factor.state === 1}
            datePublished={factor.lastPublishedDate}
          />
        )}

        {/* Outside the form on purpose: a tab button inside it is an implicit submit button, so
            switching tabs would fire the publish/unpublish submit handler. */}
        <ContentTabs items={tabs} selectedIndex={selectedTab} onChange={setSelectedTab} />

        <form onSubmit={formik.handleSubmit} className="piece-content">
          {TAB_ORDER[selectedTab] === TabsEnum.MATERIAL && (
            <MaterialInfo
              formik={formik}
              configuration={configuration}
              code={factor.code}
              onCategoryChange={handleCategoryChange}
            />
          )}
          {TAB_ORDER[selectedTab] === TabsEnum.DATA_ORIGIN && (
            <DataOrigin formik={formik} configuration={configuration} />
          )}
          {TAB_ORDER[selectedTab] === TabsEnum.EMISSIONS && <Emissions formik={formik} />}
          {TAB_ORDER[selectedTab] === TabsEnum.IMPACTS && <Impacts formik={formik} />}
          {TAB_ORDER[selectedTab] === TabsEnum.DATA_QUALITY && (
            <DataQualitySection
              formik={formik}
              configuration={configuration}
              process={factor}
              onFieldChange={handleDataQualityFieldChange}
              showCompletenessScore={false}
              referenceTechnologyTypeDescription={PACKAGING_REFERENCE_TECHNOLOGY_TYPE_DESCRIPTION}
              referenceTechnologyProxyContributionDescription={
                PACKAGING_REFERENCE_TECHNOLOGY_PROXY_CONTRIBUTION_DESCRIPTION
              }
            />
          )}

          {errors && (
            <ul className="piece-errors">
              {errors.map(([key, value]) =>
                (value as any).map(err => (
                  <li key={key + err}>
                    <b>{key}:</b> {err}
                  </li>
                ))
              )}
            </ul>
          )}

          <div className="piece-actions">
            <UnsavedChangesWarning isDirty={isDirty} />
            <Button variant="secondary" className="w-32" size="md" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              className="w-32"
              size="md"
              onClick={saveDraft}
              disabled={formik.isSubmitting}
            >
              Save
            </Button>
            {sessionStorage.getItem('permission') === 'true' && factorId !== 'new' && (
              <Button
                variant="tertiary"
                className="w-32"
                size="md"
                type="submit"
                disabled={formik.isSubmitting || isDirty}
              >
                {factor.state === 0 ? 'Publish' : 'Unpublish'}
              </Button>
            )}
          </div>
        </form>
      </div>
      <ActivityRightPanel
        activityId={factorId}
        activityType={ActivityTypes.PackagingEmissionFactors}
        auditCreatedBy={factor.researcherName}
        auditLastUpdatedBy={factor.lastUpdatedName}
        companyId={formik.values.companyId}
        isVerified={formik.values.isVerified}
        verificationComments={formik.values.verificationComments}
        verifiedByName={formik.values.verifiedByName}
        verifiedOn={formik.values.verifiedOn}
        onChange={formik.handleChange}
      />
    </>
  )
}

export default MaterialEmissionFactor
